/**
 * HFDS — Headzflex Design System
 * Component: HF-006 Product Gallery Engine
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Navigate product media.
 * - Synchronize thumbnails.
 * - Handle touch and pointer swipes.
 * - Respond to variant media changes.
 * - Provide accessible fullscreen viewing.
 * - Emit analytics-ready HFDS events.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-product-gallery]";
  const VIEWPORT_SELECTOR = "[data-hf-gallery-viewport]";
  const TRACK_SELECTOR = "[data-hf-gallery-track]";
  const SLIDE_SELECTOR = "[data-hf-gallery-slide]";
  const THUMB_SELECTOR = "[data-hf-gallery-thumbnail]";
  const PREVIOUS_SELECTOR = "[data-hf-gallery-previous]";
  const NEXT_SELECTOR = "[data-hf-gallery-next]";
  const COUNTER_CURRENT_SELECTOR =
    "[data-hf-gallery-counter-current]";
  const FULLSCREEN_SELECTOR = "[data-hf-gallery-fullscreen]";
  const DIALOG_SELECTOR = "[data-hf-gallery-dialog]";
  const DIALOG_CONTENT_SELECTOR =
    "[data-hf-gallery-dialog-content]";
  const DIALOG_CLOSE_SELECTOR =
    "[data-hf-gallery-dialog-close]";

  class HFProductGallery {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.productId = String(root.dataset.productId || "");

      this.viewport = root.querySelector(VIEWPORT_SELECTOR);
      this.track = root.querySelector(TRACK_SELECTOR);
      this.slides = Array.from(
        root.querySelectorAll(SLIDE_SELECTOR)
      );
      this.thumbnails = Array.from(
        root.querySelectorAll(THUMB_SELECTOR)
      );

      this.previousButton =
        root.querySelector(PREVIOUS_SELECTOR);
      this.nextButton =
        root.querySelector(NEXT_SELECTOR);
      this.counterCurrent =
        root.querySelector(COUNTER_CURRENT_SELECTOR);
      this.fullscreenButton =
        root.querySelector(FULLSCREEN_SELECTOR);

      this.dialog = root.querySelector(DIALOG_SELECTOR);
      this.dialogContent =
        root.querySelector(DIALOG_CONTENT_SELECTOR);
      this.dialogClose =
        root.querySelector(DIALOG_CLOSE_SELECTOR);

      this.activeIndex = 0;
      this.pointerStartX = 0;
      this.pointerCurrentX = 0;
      this.pointerId = null;
      this.dragging = false;
      this.focusCleanup = null;

      this.handleThumbnailClick =
        this.handleThumbnailClick.bind(this);
      this.handlePrevious =
        this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleViewportKeydown =
        this.handleViewportKeydown.bind(this);
      this.handlePointerDown =
        this.handlePointerDown.bind(this);
      this.handlePointerMove =
        this.handlePointerMove.bind(this);
      this.handlePointerUp =
        this.handlePointerUp.bind(this);
      this.handleVariantChange =
        this.handleVariantChange.bind(this);
      this.openFullscreen =
        this.openFullscreen.bind(this);
      this.closeFullscreen =
        this.closeFullscreen.bind(this);
      this.handleDialogClick =
        this.handleDialogClick.bind(this);
      this.handleDialogClose =
        this.handleDialogClose.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Product Gallery] HFDS core is unavailable."
        );
        return;
      }

      if (
        !(this.viewport instanceof HTMLElement) ||
        !(this.track instanceof HTMLElement) ||
        this.slides.length === 0
      ) {
        console.error(
          "[HFDS Product Gallery] Required gallery elements are missing."
        );
        return;
      }

      const initiallyActive = this.slides.findIndex(
        (slide) => slide.dataset.active === "true"
      );

      this.activeIndex =
        initiallyActive >= 0 ? initiallyActive : 0;

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener(
          "click",
          this.handleThumbnailClick
        );
      });

      this.previousButton?.addEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.addEventListener(
        "click",
        this.handleNext
      );

      this.viewport.addEventListener(
        "keydown",
        this.handleViewportKeydown
      );

      this.viewport.addEventListener(
        "pointerdown",
        this.handlePointerDown
      );

      this.viewport.addEventListener(
        "pointermove",
        this.handlePointerMove
      );

      this.viewport.addEventListener(
        "pointerup",
        this.handlePointerUp
      );

      this.viewport.addEventListener(
        "pointercancel",
        this.handlePointerUp
      );

      this.fullscreenButton?.addEventListener(
        "click",
        this.openFullscreen
      );

      this.dialogClose?.addEventListener(
        "click",
        this.closeFullscreen
      );

      this.dialog?.addEventListener(
        "click",
        this.handleDialogClick
      );

      this.dialog?.addEventListener(
        "close",
        this.handleDialogClose
      );

      document.addEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      this.setActive(this.activeIndex, {
        emitEvent: false,
        announce: false,
        focusThumbnail: false
      });

      this.root.dataset.hfInitialized = "true";
    }

    /**
     * @param {number} index
     * @returns {number}
     */
    normalizeIndex(index) {
      if (this.slides.length === 0) {
        return 0;
      }

      if (index < 0) {
        return this.slides.length - 1;
      }

      if (index >= this.slides.length) {
        return 0;
      }

      return index;
    }

    /**
     * @param {number} index
     * @param {Object} options
     */
    setActive(
      index,
      {
        emitEvent = true,
        announce = true,
        focusThumbnail = false
      } = {}
    ) {
      const nextIndex = this.normalizeIndex(index);
      const previousIndex = this.activeIndex;

      this.activeIndex = nextIndex;

      this.track.style.transform =
        `translate3d(-${nextIndex * 100}%, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;

        slide.dataset.active = String(active);
        slide.setAttribute(
          "aria-hidden",
          String(!active)
        );

        this.pauseInactiveMedia(slide, active);
      });

      this.thumbnails.forEach(
        (thumbnail, thumbnailIndex) => {
          const active = thumbnailIndex === nextIndex;

          thumbnail.classList.toggle(
            "is-active",
            active
          );

          thumbnail.setAttribute(
            "aria-current",
            active ? "true" : "false"
          );

          if (active) {
            thumbnail.scrollIntoView({
              behavior: window.HFDS.prefersReducedMotion()
                ? "auto"
                : "smooth",
              block: "nearest",
              inline: "nearest"
            });

            if (focusThumbnail) {
              thumbnail.focus();
            }
          }
        }
      );

      if (this.counterCurrent) {
        this.counterCurrent.textContent =
          String(nextIndex + 1);
      }

      this.updateControlStates();

      if (emitEvent) {
        this.emitMediaEvent(previousIndex, nextIndex);
      }

      if (announce) {
        const activeSlide = this.slides[nextIndex];
        const label =
          activeSlide?.dataset.mediaLabel ||
          `Media ${nextIndex + 1}`;

        window.HFDS.announce(
          `${label}. Item ${nextIndex + 1} of ${this.slides.length}.`,
          "polite"
        );
      }
    }

    /**
     * Pauses media when its slide becomes inactive.
     *
     * @param {HTMLElement} slide
     * @param {boolean} active
     */
    pauseInactiveMedia(slide, active) {
      if (active) {
        return;
      }

      slide.querySelectorAll("video").forEach((video) => {
        if (typeof video.pause === "function") {
          video.pause();
        }
      });

      slide.querySelectorAll("model-viewer").forEach(
        (modelViewer) => {
          if (
            typeof modelViewer.pause === "function"
          ) {
            modelViewer.pause();
          }
        }
      );
    }

    updateControlStates() {
      const singleItem = this.slides.length <= 1;

      if (this.previousButton) {
        this.previousButton.disabled = singleItem;
      }

      if (this.nextButton) {
        this.nextButton.disabled = singleItem;
      }
    }

    handleThumbnailClick(event) {
      const thumbnail = event.currentTarget;
      const index = Number(thumbnail.dataset.mediaIndex);

      if (!Number.isInteger(index)) {
        return;
      }

      this.setActive(index);
    }

    handlePrevious() {
      this.setActive(this.activeIndex - 1);
    }

    handleNext() {
      this.setActive(this.activeIndex + 1);
    }

    handleViewportKeydown(event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.handlePrevious();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.handleNext();
      }

      if (event.key === "Home") {
        event.preventDefault();
        this.setActive(0);
      }

      if (event.key === "End") {
        event.preventDefault();
        this.setActive(this.slides.length - 1);
      }

      if (
        event.key === "Enter" &&
        this.fullscreenButton
      ) {
        event.preventDefault();
        this.openFullscreen();
      }
    }

    handlePointerDown(event) {
      if (
        event.pointerType === "mouse" &&
        event.button !== 0
      ) {
        return;
      }

      if (
        event.target.closest(
          "button, a, video, iframe, model-viewer"
        )
      ) {
        return;
      }

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerCurrentX = event.clientX;
      this.dragging = true;

      this.root.classList.add("is-dragging");

      this.viewport.setPointerCapture?.(
        event.pointerId
      );
    }

    handlePointerMove(event) {
      if (
        !this.dragging ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }

      this.pointerCurrentX = event.clientX;

      const distance =
        this.pointerCurrentX - this.pointerStartX;

      const viewportWidth =
        this.viewport.getBoundingClientRect().width || 1;

      const percentage =
        (distance / viewportWidth) * 100;

      this.track.style.transform =
        `translate3d(calc(-${this.activeIndex * 100}% + ${percentage}%), 0, 0)`;
    }

    handlePointerUp(event) {
      if (
        !this.dragging ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }

      const distance =
        this.pointerCurrentX - this.pointerStartX;

      const threshold = Math.min(
        80,
        this.viewport.getBoundingClientRect().width *
          0.16
      );

      this.dragging = false;
      this.pointerId = null;
      this.root.classList.remove("is-dragging");

      if (Math.abs(distance) >= threshold) {
        if (distance < 0) {
          this.handleNext();
        } else {
          this.handlePrevious();
        }

        return;
      }

      this.setActive(this.activeIndex, {
        emitEvent: false,
        announce: false
      });
    }

    /**
     * Responds to the selected variant's featured media.
     *
     * @param {CustomEvent} event
     */
    handleVariantChange(event) {
      const detail = event.detail || {};

      if (
        this.productId &&
        detail.productId &&
        String(detail.productId) !== this.productId
      ) {
        return;
      }

      const mediaId =
        detail.variant?.featured_media_id;

      if (!mediaId) {
        return;
      }

      const index = this.slides.findIndex(
        (slide) =>
          String(slide.dataset.mediaId) ===
          String(mediaId)
      );

      if (index >= 0) {
        this.setActive(index);
      }
    }

    openFullscreen() {
      if (
        !(this.dialog instanceof HTMLDialogElement) ||
        !(this.dialogContent instanceof HTMLElement)
      ) {
        return;
      }

      const activeSlide = this.slides[this.activeIndex];
      const sourceMedia = activeSlide?.querySelector(
        "[data-hf-gallery-media-content]"
      );

      if (!sourceMedia) {
        return;
      }

      this.dialogContent.innerHTML = "";

      const clone = sourceMedia.cloneNode(true);

      clone
        .querySelectorAll("[id]")
        .forEach((element) => {
          element.removeAttribute("id");
        });

      clone
        .querySelectorAll("[loading]")
        .forEach((element) => {
          element.removeAttribute("loading");
        });

      this.dialogContent.appendChild(clone);

      this.dialog.showModal();

      window.HFDS.lockScroll();

      this.focusCleanup = window.HFDS.trapFocus(
        this.dialog
      );

      window.HFDS.emit(
        "gallery:fullscreen-open",
        this.getMediaDetail(),
        this.root
      );
    }

    closeFullscreen() {
      if (
        this.dialog instanceof HTMLDialogElement &&
        this.dialog.open
      ) {
        this.dialog.close();
      }
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) {
        this.closeFullscreen();
      }
    }

    handleDialogClose() {
      this.dialogContent?.querySelectorAll("video").forEach(
        (video) => video.pause?.()
      );

      if (this.dialogContent) {
        this.dialogContent.innerHTML = "";
      }

      window.HFDS.unlockScroll();

      if (typeof this.focusCleanup === "function") {
        this.focusCleanup();
      }

      this.focusCleanup = null;

      window.HFDS.emit(
        "gallery:fullscreen-close",
        this.getMediaDetail(),
        this.root
      );
    }

    getMediaDetail() {
      const slide = this.slides[this.activeIndex];

      return {
        productId: this.productId,
        index: this.activeIndex,
        position: this.activeIndex + 1,
        total: this.slides.length,
        mediaId: slide?.dataset.mediaId || "",
        mediaType: slide?.dataset.mediaType || "",
        mediaLabel: slide?.dataset.mediaLabel || ""
      };
    }

    emitMediaEvent(previousIndex, nextIndex) {
      window.HFDS.emit(
        "gallery:media-change",
        {
          ...this.getMediaDetail(),
          previousIndex,
          currentIndex: nextIndex
        },
        this.root
      );
    }

    destroy() {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.removeEventListener(
          "click",
          this.handleThumbnailClick
        );
      });

      this.previousButton?.removeEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        "click",
        this.handleNext
      );

      this.viewport?.removeEventListener(
        "keydown",
        this.handleViewportKeydown
      );

      this.viewport?.removeEventListener(
        "pointerdown",
        this.handlePointerDown
      );

      this.viewport?.removeEventListener(
        "pointermove",
        this.handlePointerMove
      );

      this.viewport?.removeEventListener(
        "pointerup",
        this.handlePointerUp
      );

      this.viewport?.removeEventListener(
        "pointercancel",
        this.handlePointerUp
      );

      this.fullscreenButton?.removeEventListener(
        "click",
        this.openFullscreen
      );

      this.dialogClose?.removeEventListener(
        "click",
        this.closeFullscreen
      );

      this.dialog?.removeEventListener(
        "click",
        this.handleDialogClick
      );

      this.dialog?.removeEventListener(
        "close",
        this.handleDialogClose
      );

      document.removeEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      delete this.root.dataset.hfInitialized;
    }
  }

  function initializeProductGalleries(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfProductGallery =
          new HFProductGallery(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeProductGalleries(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeProductGalleries(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfProductGallery?.destroy();
        });
    }
  );

  window.HFDSProductGallery = Object.freeze({
    version: "1.0.0",
    initialize: initializeProductGalleries
  });
})();